/**
 * Bootstrap to MUI v5 compatibility layer
 * This file provides drop-in replacements for Bootstrap components using MUI
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  LinearProgress,
  Box,
  FormControl,
  FormLabel,
  FormGroup,
  Card as MuiCard,
  CardContent,
  CardActions,
  CardHeader,
  Tabs as MuiTabs,
  Tab as MuiTab,
  Alert as MuiAlert,
  AppBar,
  Toolbar,
  Typography,
  Button as MuiButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MuiTextField from '@mui/material/TextField';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import CustomModal from '../frontend/dashboard/components/CustomModal';

// Modal → CustomModal (with fallback to Dialog)
export const Modal = ({ show, onHide, children, size, centered, ...props }) => {
  return (
    <CustomModal
      show={show}
      onHide={onHide}
      size={size || 'md'}
      centered={centered}
      {...props}
    >
      {children}
    </CustomModal>
  );
};

Modal.Header = CustomModal.Header;
Modal.Title = CustomModal.Title;
Modal.Body = CustomModal.Body;
Modal.Footer = CustomModal.Footer;

// Row → Grid container
export const Row = ({ children, ...props }) => {
  return (
    <Grid container spacing={2} {...props}>
      {children}
    </Grid>
  );
};

// Col → Grid item
export const Col = ({ xs, sm, md, lg, xl, children, ...props }) => {
  return (
    <Grid
      item
      xs={xs || 12}
      sm={sm}
      md={md}
      lg={lg}
      xl={xl}
      {...props}
    >
      {children}
    </Grid>
  );
};

// Container → Box with container styling
export const Container = styled(Box)(({ theme }) => ({
  width: '100%',
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    maxWidth: 540,
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: 720,
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: 960,
  },
  [theme.breakpoints.up('xl')]: {
    maxWidth: 1140,
  },
}));

// ProgressBar → LinearProgress
export const ProgressBar = ({ now, label, variant, ...props }) => {
  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <LinearProgress
        variant={variant === 'indeterminate' ? 'indeterminate' : 'determinate'}
        value={now}
        {...props}
      />
      {label && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {label}
        </Box>
      )}
    </Box>
  );
};

// Form components using MUI
export const Form = ({ children, onSubmit, ...props }) => {
  return (
    <form onSubmit={onSubmit} {...props}>
      {children}
    </form>
  );
};

Form.Group = ({ children, controlId, ...props }) => {
  return (
    <FormControl fullWidth margin="normal" {...props}>
      {children}
    </FormControl>
  );
};

Form.Label = ({ children, ...props }) => {
  return <FormLabel {...props}>{children}</FormLabel>;
};

Form.Control = React.forwardRef(({ type, as, isInvalid, ...props }, ref) => {
  if (as === 'select') {
    return (
      <Box component="select" sx={{ width: '100%', padding: '8px', borderRadius: '4px' }} ref={ref} {...props}>
        {props.children}
      </Box>
    );
  }
  if (as === 'textarea') {
    return <MuiTextField multiline rows={4} fullWidth error={isInvalid} inputRef={ref} {...props} />;
  }
  return <MuiTextField type={type} fullWidth error={isInvalid} inputRef={ref} {...props} />;
});

Form.Control.Feedback = ({ children, type, tooltip, ...props }) => {
  return (
    <Box
      sx={{
        color: type === 'invalid' ? 'error.main' : 'success.main',
        fontSize: '0.875rem',
        marginTop: '0.25rem',
        position: tooltip ? 'absolute' : 'relative',
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

Form.Check = ({ type, label, ...props }) => {
  return (
    <FormGroup>
      <label>
        <input type={type || 'checkbox'} {...props} />
        {label && <span style={{ marginLeft: 8 }}>{label}</span>}
      </label>
    </FormGroup>
  );
};

Form.Row = ({ children, ...props }) => {
  return (
    <Grid container spacing={2} {...props}>
      {children}
    </Grid>
  );
};

Form.File = ({ children, ...props }) => {
  return <Box {...props}>{children}</Box>;
};

Form.File.Label = ({ children, ...props }) => {
  return (
    <Box component="label" sx={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }} {...props}>
      {children}
    </Box>
  );
};

Form.File.Input = React.forwardRef(({ className, ...props }, ref) => {
  return <input type="file" ref={ref} {...props} />;
});

// Button using MUI
export const Button = ({ variant, size, block, children, ...props }) => {
  const muiVariant = variant === 'primary' || variant === 'secondary' || variant === 'success'
    ? 'contained'
    : variant === 'outline-primary' || variant === 'outline-secondary'
    ? 'outlined'
    : variant === 'link'
    ? 'text'
    : 'contained';

  const muiColor = variant?.includes('primary') ? 'primary'
    : variant?.includes('secondary') ? 'secondary'
    : variant?.includes('success') ? 'success'
    : variant?.includes('danger') ? 'error'
    : 'primary';

  return (
    <MuiButton
      variant={muiVariant}
      color={muiColor}
      size={size}
      fullWidth={block}
      {...props}
    >
      {children}
    </MuiButton>
  );
};

// Image
export const Image = ({ src, alt, fluid, rounded, thumbnail, ...props }) => {
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        maxWidth: fluid ? '100%' : undefined,
        height: 'auto',
        borderRadius: rounded ? 1 : undefined,
        border: thumbnail ? '1px solid #dee2e6' : undefined,
        padding: thumbnail ? '0.25rem' : undefined,
      }}
      {...props}
    />
  );
};

// Card using MUI
export const Card = ({ children, ...props }) => {
  return <MuiCard {...props}>{children}</MuiCard>;
};

Card.Body = ({ children, ...props }) => {
  return <CardContent {...props}>{children}</CardContent>;
};

Card.Header = ({ children, ...props }) => {
  return <CardHeader title={children} {...props} />;
};

Card.Footer = ({ children, ...props }) => {
  return <CardActions {...props}>{children}</CardActions>;
};

Card.Title = ({ children, ...props }) => {
  return <Box component="h5" {...props}>{children}</Box>;
};

Card.Text = ({ children, ...props }) => {
  return <Box component="p" {...props}>{children}</Box>;
};

// Tabs using MUI
export const Tabs = ({ activeKey, onSelect, children, ...props }) => {
  const [value, setValue] = React.useState(activeKey || '0');

  React.useEffect(() => {
    if (activeKey !== undefined) {
      setValue(activeKey);
    }
  }, [activeKey]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (onSelect) {
      onSelect(newValue);
    }
  };

  return (
    <TabContext value={value}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <MuiTabs
          value={value}
          onChange={handleChange}
          TabIndicatorProps={{
            sx: {
              height: 3,
              borderRadius: '3px 3px 0 0',
            }
          }}
          {...props}
        >
          {React.Children.map(children, (child, index) => {
            if (child?.props?.eventKey) {
              return (
                <MuiTab
                  label={child.props.title}
                  value={child.props.eventKey}
                  key={index}
                  sx={{
                    '& .MuiTab-wrapper': {
                      alignItems: 'flex-start',
                    }
                  }}
                />
              );
            }
            return null;
          })}
        </MuiTabs>
      </Box>
      {React.Children.map(children, (child, index) => {
        if (child?.props?.eventKey) {
          return (
            <TabPanel value={child.props.eventKey} sx={{ p: 3 }} key={index}>
              {child.props.children}
            </TabPanel>
          );
        }
        return null;
      })}
    </TabContext>
  );
};

export const Tab = ({ eventKey, title, children }) => {
  // This is just a placeholder, actual rendering happens in Tabs
  return null;
};

// Alert using MUI
export const Alert = ({ variant, show, children, ...props }) => {
  const severity = variant === 'danger' ? 'error'
    : variant === 'warning' ? 'warning'
    : variant === 'info' ? 'info'
    : variant === 'success' ? 'success'
    : variant === 'light' ? 'info'
    : 'info';

  if (show === false) return null;

  return (
    <MuiAlert severity={severity} {...props}>
      {children}
    </MuiAlert>
  );
};

Alert.Heading = ({ children, ...props }) => {
  return <Box component="h5" fontWeight="bold" {...props}>{children}</Box>;
};

// Navbar using MUI
export const Navbar = ({ bg, variant, expand, children, ...props }) => {
  return (
    <AppBar position="static" color={bg === 'dark' ? 'primary' : 'default'} {...props}>
      <Toolbar>{children}</Toolbar>
    </AppBar>
  );
};

Navbar.Brand = ({ children, ...props }) => {
  return (
    <Typography variant="h6" component="div" sx={{ flexGrow: 0 }} {...props}>
      {children}
    </Typography>
  );
};

Navbar.Toggle = () => null; // MUI AppBar doesn't need toggle in the same way

Navbar.Collapse = ({ children, ...props }) => {
  return <Box sx={{ flexGrow: 1, display: 'flex' }} {...props}>{children}</Box>;
};

export const Nav = ({ children, className, variant, ...props }) => {
  return <Box sx={{ display: 'flex', gap: 2 }} {...props}>{children}</Box>;
};

Nav.Link = ({ children, href, ...props }) => {
  return (
    <Box component="a" href={href} sx={{ color: 'inherit', textDecoration: 'none' }} {...props}>
      {children}
    </Box>
  );
};

Nav.Item = ({ children, ...props }) => {
  return <Box {...props}>{children}</Box>;
};

// Carousel - Simple implementation
export const Carousel = ({ children, ...props }) => {
  return (
    <Box {...props}>
      {React.Children.map(children, (child, index) => (
        <Box key={index}>
          {child}
        </Box>
      ))}
    </Box>
  );
};

Carousel.Item = ({ children, ...props }) => {
  return <Box {...props}>{children}</Box>;
};
